/// <reference types="vite/client" />

export const GA_MEASUREMENT_ID = "G-M3VQMMK9Z3";

type GtagFn = (...args: unknown[]) => void;

export function getGtag(): GtagFn | null {
  const w = window as unknown as { gtag?: GtagFn };
  return typeof w.gtag === "function" ? w.gtag : null;
}

/** Aligns with existing env toggles used for GA in production vs staging/dev. */
export function gaEnabled(): boolean {
  const flag = String((import.meta.env as { VITE_GA_ENABLED?: string }).VITE_GA_ENABLED ?? "").toLowerCase();
  if (flag === "false") return false;
  if (flag === "true") return true;

  if (import.meta.env.PROD) return true;
  return String((import.meta.env as { VITE_GA_ENABLE_IN_DEV?: string }).VITE_GA_ENABLE_IN_DEV ?? "").toLowerCase() === "true";
}

function logDev(eventKind: "page_view" | "event", name: string, payload?: Record<string, unknown>) {
  if (!import.meta.env.DEV) return;
  if (payload !== undefined) {
    // eslint-disable-next-line no-console
    console.debug(`[tracking] ${eventKind}:`, name, payload);
  } else {
    // eslint-disable-next-line no-console
    console.debug(`[tracking] ${eventKind}:`, name);
  }
}

/**
 * GA4 SPA page views. Safe when `gtag` is missing; no-ops when GA is disabled by env.
 */
export function trackPageView(path: string, title?: string) {
  const pageTitle = title ?? document.title;
  const payload = {
    page_path: path,
    page_title: pageTitle,
    page_location: typeof window !== "undefined" ? window.location.href : undefined,
  };

  logDev("page_view", path, payload);

  if (!gaEnabled()) return;
  const gtag = getGtag();
  if (!gtag) return;

  try {
    gtag("event", "page_view", payload);
  } catch {
    // fail silently
  }
}

/**
 * Custom GA4 events. Fails silently if `gtag` throws or is unavailable.
 * In development, logs every call to the console (even when GA is disabled).
 */
export function trackEvent(event: string, meta?: Record<string, unknown>) {
  logDev("event", event, meta);

  if (!gaEnabled()) return;
  const gtag = getGtag();
  if (!gtag) return;

  try {
    gtag("event", event, meta ?? {});
  } catch {
    // fail silently
  }
}
